import { Fragment, useEffect, useMemo, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import { rgba } from 'polished';
import styled from 'styled-components';

import { StarIcon } from '@treams/treams-icons';

import { Skeleton } from './Skeleton';

const Container = styled(Box)`
	position: relative;
`;

const Svg = styled.svg`
	display: block;
`;

const Circle = styled.circle<{ skeleton?: boolean }>`
	stroke: ${(props) =>
		props.skeleton ? props.theme.colors.lightBlueDark : props.theme.colors.slateBlue};
	fill: transparent;
`;

const Tick = styled.text`
	fill: ${(props) => props.theme.colors.primary};
	font-weight: bold;
	font-size: 12px;
	text-anchor: middle;
`;

const Region = styled.path<{ skeleton?: boolean }>`
	stroke: ${(props) => props.theme.colors.primary};
	stroke-width: 2px;
	fill: ${(props) => rgba(props.theme.colors.primary, 0.25)};

	${(props) =>
		props.skeleton &&
		`
		stroke: none;
		fill: ${rgba(props.theme.colors.lightBlueDark, 0.5)};
		transition: 1s;
	`}
`;

const Label = styled(Box)<{ x: number; y: number }>`
	position: absolute;
	left: ${(props) => props.x}px;
	top: ${(props) => props.y}px;
`;

const Star = styled.g`
	transform-origin: center;
	transform: scale(10%) translate(-40px, -5px);

	svg {
		width: 16px;
		height: 16px;

		path {
			fill: #f7b500;
		}
	}
`;

function computeRegion(scores: number[]): [number, number][] {
	const angle = (Math.PI / scores.length) * 2;

	return scores.map((score, i) => [
		(Math.sin(angle * (i + 2)) * score) / 5,
		(Math.cos(angle * (i + 2)) * score) / 5,
	]);
}

export function RadarChart(props: { labels: string[]; scores: number[] }) {
	const size = 200;

	const path = computeRegion(props.scores);

	return (
		<Flex py={4} justifyContent="center">
			<Container>
				<Svg width={size} height={size}>
					{[1, 2, 3, 4, 5].map((i) => (
						<Fragment key={i}>
							<Circle cx={size / 2} cy={size / 2} r={(size / 2 / 5) * i - 1} />
							<Tick x={size / 2 + (size / 2 / 5) * (i - 0.5)} y={size / 2}>
								{i}
							</Tick>
						</Fragment>
					))}
					<Star>
						<StarIcon />
					</Star>
					<Region
						d={`M ${path[path.length - 1][0] * (size / 2 - 2) + size / 2} ${
							path[path.length - 1][1] * (size / 2 - 2) + size / 2
						} ${path.map(
							([x, y]) => `L ${x * (size / 2 - 2) + size / 2} ${y * (size / 2 - 2) + size / 2}`,
						)}`}
					/>
				</Svg>
				{props.labels.map((label, i) => {
					const angle = (Math.PI / props.labels.length) * 2;
					const x = Math.sin(angle * (i + 2)) * (size / 2 + 10);
					const y = Math.cos(angle * (i + 2)) * (size / 2 + 10);

					return (
						<Label
							key={i}
							x={x + size / 2}
							y={y + size / 2}
							style={{
								transform: [
									`translateX(${Math.round(x) === 0 ? '-50%' : x < 0 ? '-100%' : '0%'})`,
									`translateY(${Math.round(y) === 0 ? '-50%' : y < 0 ? '-100%' : '0%'})`,
								].join(' '),
							}}
						>
							{label}
						</Label>
					);
				})}
			</Container>
		</Flex>
	);
}

function RadarChartSkeleton() {
	const size = 200;

	const count = 4;

	const [toggle, setToggle] = useState(false);

	const path = useMemo(
		() => computeRegion([...new Array(count)].map(() => Math.round(Math.random() * 4 + 1))),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[toggle],
	);

	useEffect(() => {
		setToggle((toggle) => !toggle);

		const interval = setInterval(() => setToggle((toggle) => !toggle), 1000);
		return () => clearInterval(interval);
	}, []);

	return (
		<Flex py={4} justifyContent="center">
			<Container>
				<Svg width={size} height={size}>
					{[1, 2, 3, 4, 5].map((i) => (
						<Fragment key={i}>
							<Circle cx={size / 2} cy={size / 2} r={(size / 2 / 5) * i - 1} skeleton />
						</Fragment>
					))}
					<Region
						skeleton
						d={`M ${path[path.length - 1][0] * (size / 2 - 2) + size / 2} ${
							path[path.length - 1][1] * (size / 2 - 2) + size / 2
						} ${path.map(
							([x, y]) => `L ${x * (size / 2 - 2) + size / 2} ${y * (size / 2 - 2) + size / 2}`,
						)}`}
					/>
				</Svg>
				{[...new Array(count)].map((_, i) => {
					const angle = (Math.PI / count) * 2;
					const x = Math.sin(angle * (i + 2)) * (size / 2 + 10);
					const y = Math.cos(angle * (i + 2)) * (size / 2 + 10);

					return (
						<Label
							key={i}
							x={x + size / 2}
							y={y + size / 2}
							style={{
								transform: [
									`translateX(${Math.round(x) === 0 ? '-50%' : x < 0 ? '-100%' : '0%'})`,
									`translateY(${Math.round(y) === 0 ? '-50%' : y < 0 ? '-100%' : '0%'})`,
								].join(' '),
							}}
						>
							<Skeleton.Box width={40} height={20} />
						</Label>
					);
				})}
			</Container>
		</Flex>
	);
}

RadarChart.Skeleton = RadarChartSkeleton;
